/*
  Keeps the floating WhatsApp button (snippets/leza-whatsapp.liquid) off interactive content.
  The button keeps its size, look, animation and resting place (including its offset above the phone
  purchase bar). When, once scrolling / resizing has settled, it would hide a small control (its centre
  or at least half of it: a button, link, field or swatch), the footer copyright line or the desktop
  slim add-to-cart bar, it is lifted just above it — never more than 20rem or a quarter of the screen —
  through the CSS `translate` property, so its hover transform still works and nothing is laid out
  again (no layout shift). It drops back when the space below is clear. Left alone: clickable areas
  taller than 120px, "stretched" links whose ::after overlay makes a whole card clickable (a product card
  title), and wide controls whose uncovered part is still easy to reach (a full-width Buy it now).
*/
(() => {
  const button = document.querySelector('.leza-whatsapp');
  if (!button || window.lezaFloatClearance) return;
  window.lezaFloatClearance = true;

  const CONTROLS =
    'a[href], button, input:not([type="hidden"]), select, textarea, summary, label, [role="button"], [role="link"], [tabindex]:not([tabindex="-1"])';
  const GAP = 8; // px kept between the button and the control it clears
  const MAX_CONTROL_HEIGHT = 120; // taller clickable areas are not treated as obstacles
  const ALWAYS = '.leza-footer__copy'; // kept clear even though it spans the width
  let lift = 0;
  let timer = 0;

  const maxLift = () => Math.min(200, window.innerHeight * 0.25);

  // A link whose ::after covers its card (Dawn / Leza card titles): the whole card is its click area.
  const isStretched = (control) => {
    if (control.tagName !== 'A') return false;
    const after = getComputedStyle(control, '::after');
    return after.content !== 'none' && after.position === 'absolute';
  };

  // Topmost page control (ignoring floating UI) at a point: { element, rect, always } or null.
  const controlAt = (x, y) => {
    for (const element of document.elementsFromPoint(x, y)) {
      if (element === document.documentElement || element === document.body) return null;
      const bar = element.closest('.leza-sticky-atc');
      if (bar) {
        if (bar.classList.contains('is-visible')) return { element: bar, rect: bar.getBoundingClientRect(), always: true };
        continue;
      }
      if (element.closest('.leza-float')) continue;
      const line = element.closest(ALWAYS);
      if (line) return { element: line, rect: line.getBoundingClientRect(), always: true };
      const control = element.closest(CONTROLS);
      if (!control) return null;
      const rect = control.getBoundingClientRect();
      const inside = x >= rect.left && x <= rect.right && y >= rect.top && y <= rect.bottom;
      if (!inside || rect.height > MAX_CONTROL_HEIGHT || isStretched(control)) return null;
      return { element: control, rect, always: false };
    }
    return null;
  };

  // Would the button (box) hide this control? Only if it covers its centre or at least half of it:
  // the right end of a full-width button under the button is still easy to reach.
  const hides = ({ rect, always }, box) => {
    if (always) return true;
    const cx = (rect.left + rect.right) / 2;
    const cy = (rect.top + rect.bottom) / 2;
    if (cx >= box.left && cx <= box.right && cy >= box.top && cy <= box.bottom) return true;
    const w = Math.max(0, Math.min(rect.right, box.right) - Math.max(rect.left, box.left));
    const h = Math.max(0, Math.min(rect.bottom, box.bottom) - Math.max(rect.top, box.top));
    return (w * h) / (rect.width * rect.height || 1) >= 0.5;
  };

  // Highest top of a control the button would hide when raised by `offset`.
  const obstacleTop = (base, offset) => {
    const box = { left: base.left, right: base.left + base.width, top: base.top - offset - GAP, bottom: base.bottom - offset + GAP };
    let highest = null;
    for (const fx of [0.03, 0.15, 0.5, 0.85, 0.97]) {
      for (const fy of [0, 0.5, 1]) {
        const x = box.left + base.width * fx;
        const y = Math.min(window.innerHeight - 1, Math.max(0, box.top + (box.bottom - box.top) * fy));
        const control = controlAt(x, y);
        if (control && hides(control, box) && (highest === null || control.rect.top < highest)) highest = control.rect.top;
      }
    }
    return highest;
  };

  const update = () => {
    timer = 0;
    const style = getComputedStyle(button);
    if (style.visibility === 'hidden' || style.display === 'none') return;
    const now = button.getBoundingClientRect();
    // Resting box: undo the current lift (translate is applied after layout).
    const base = { left: now.left, width: now.width, top: now.top + lift, bottom: now.bottom + lift };
    let next = 0;
    for (let i = 0; i < 4; i += 1) {
      const top = obstacleTop(base, next);
      if (top === null) break;
      const needed = Math.ceil(base.bottom - top + GAP);
      if (needed <= next || needed > maxLift()) {
        // Cannot clear it within the limit: stay at rest rather than jump far up the page.
        if (needed > maxLift()) next = 0;
        break;
      }
      next = needed;
    }
    if (next !== lift) {
      lift = next;
      button.style.setProperty('--leza-float-lift', `${lift}px`);
    }
  };

  // Debounced: nothing runs while the page keeps scrolling / resizing; one check 120ms after it stops,
  // in an animation frame (all reads first, one style write at the end).
  // Not before the page has loaded and the browser is idle: during loading every check forces a layout
  // of a page that is still changing (costly on phones); the button simply rests until then.
  let ready = false;
  const schedule = () => {
    if (!ready) return;
    clearTimeout(timer);
    timer = setTimeout(() => requestAnimationFrame(update), 120);
  };
  const start = () => {
    ready = true;
    schedule();
  };
  const whenIdle = () => ('requestIdleCallback' in window ? requestIdleCallback(start, { timeout: 3000 }) : setTimeout(start, 1500));
  if (document.readyState === 'complete') whenIdle();
  else window.addEventListener('load', whenIdle, { once: true });

  // Only entrance effects that move content into place (leza-reveal, Dawn scroll-trigger), not every hover.
  const onSettle = (event) => {
    if (event.target instanceof Element && event.target.matches('.leza-reveal, .leza-reveal-child, .scroll-trigger')) schedule();
  };

  window.addEventListener('scroll', schedule, { passive: true });
  window.addEventListener('resize', schedule);
  if ('ResizeObserver' in window) new ResizeObserver(schedule).observe(document.body);
  // Revealed content only reaches its place when its entrance transition / animation ends.
  document.addEventListener('transitionend', onSettle, true);
  document.addEventListener('animationend', onSettle, true);
  // Drawers / modals and the desktop slim bar toggle classes on <body>.
  new MutationObserver(schedule).observe(document.body, { attributes: true, attributeFilter: ['class'] });
})();
